import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { LrwSessionFormValues, lrwSessionFormSchema as schema } from '../common/lrw-session-form';
import { lrwSessionStore } from 'store';
import { Timestamp } from '@britishcouncil/common-core';
import { useCallback, useMemo } from 'react';
import { useNavigationContext, useSafeMutation, useServerErrors } from 'hooks';
import { formatApiDate, parseServerErrors } from 'utils';
import { useNavigate, generatePath } from 'react-router';
import { ROUTING_PATHS } from 'const';
import {
  getMinLrwSessionEndTime,
  toApiSessionTime,
} from '../common/lrw-session-form/lrw-session-time';

export const useCreateLrwSession = () => {
  const { locationId } = useNavigationContext();
  const navigate = useNavigate();
  const { lrwSessionDetails } = ROUTING_PATHS;

  const form = useForm<LrwSessionFormValues>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {},
  });

  const [lrwSessionDate, startTime, venueId] = form.watch([
    'sessionDate',
    'sessionStartTime',
    'venueId',
  ]);
  const minEndTime = getMinLrwSessionEndTime(startTime);

  const sessionDate = useMemo(() => {
    return lrwSessionDate ? Timestamp.toISODate(lrwSessionDate) : undefined;
  }, [lrwSessionDate]);

  const { data: availableVenues, isFetching: isFetchingVenues } =
    lrwSessionStore.service.useGetAvailableVenuesForLrwSessionQuery(
      {
        sessionDate: sessionDate!,
        locationId: locationId!,
      },
      { skip: !sessionDate || !locationId }
    );

  const { data: availableDates, isFetching: isFetchingDates } =
    lrwSessionStore.service.useGetAvailableDatesForLrwSessionQuery(
      {
        locationId: locationId!,
      },
      { skip: !locationId, refetchOnMountOrArgChange: true }
    );

  const { venueSupportsOnComputer, venueSupportsOnPaper } = useMemo(() => {
    if (!venueId || !availableVenues) {
      return {
        venueSupportsOnComputer: false,
        venueSupportsOnPaper: false,
      };
    }

    const venue = availableVenues.find((x) => x.id === venueId);
    return {
      venueSupportsOnComputer: venue?.supportsOnComputer ?? false,
      venueSupportsOnPaper: venue?.supportsOnPaper ?? false,
    };
  }, [venueId, availableVenues]);

  const isDateDisabled = useMemo(() => {
    const enabledDates = new Set((availableDates ?? []).map((x) => x.date.split('T')[0]));
    return (date: Date) => !enabledDates.has(formatApiDate(date));
  }, [availableDates]);

  const [createLrwSession, { isLoading: isCreating, isSuccess: created, error: createError }] =
    lrwSessionStore.service.useCreateLrwSessionMutation();

  const goToLrwSessionDetails = useCallback(
    (sessionId: number) => navigate(generatePath(lrwSessionDetails.absolutePath, { sessionId })),
    [navigate, lrwSessionDetails.absolutePath]
  );

  const { execute: executeCreateLrwSession } = useSafeMutation(createLrwSession, {
    successToastMessage: 'Changes saved successfully',
    onSuccess: (lrwSessionId) => goToLrwSessionDetails(lrwSessionId),
  });

  const serverErrors = useMemo(() => parseServerErrors(createError), [createError]);

  const { generalErrors, clearServerErrors } = useServerErrors(
    form.getValues(),
    serverErrors,
    form.setError
  );

  const handleSubmit = (data: LrwSessionFormValues) => {
    clearServerErrors();

    executeCreateLrwSession({
      sessionStartTime: toApiSessionTime(data.sessionStartTime),
      sessionEndTime: toApiSessionTime(data.sessionEndTime),
      sessionDate: Timestamp.toISODate(data.sessionDate),
      testTakerAdmissionTimeMinutes: data.testTakerAdmissionTimeMinutes ?? 0,
      venueId: data.venueId,
      onComputerEnabled: data.onComputerEnabled ?? false,
      onPaperEnabled: data.onPaperEnabled ?? false,
    });
  };

  return {
    form,
    handleSubmit,
    generalErrors,
    venueOptions: availableVenues?.map((x) => ({ text: x.name, value: x.id })) ?? [],
    isFetchingVenues,
    isFetchingDates,
    areDateOptionsReady: !!availableDates && !isFetchingDates,
    areVenueOptionsReady: !!availableVenues && !isFetchingVenues,
    isCreating,
    created,
    minEndTime,
    isDateDisabled,
    venueSupportsOnPaper,
    venueSupportsOnComputer,
    availableVenues,
  };
};
